import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Building2, Plus, Users, UserCog, Trash2, Edit, Key } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminOffices() {
  const { token, user } = useAuth();
  const { currentTemplate } = useTheme();
  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';

  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editOffice, setEditOffice] = useState(null);
  const [form, setForm] = useState({ name: '', admin_email: '', admin_password: '', admin_first_name: '', admin_last_name: '', can_manage_placements: false, can_manage_news: false });

  const fetchOffices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/offices`, { headers: { Authorization: `Bearer ${token}` } });
      setOffices(res.data || []);
    } catch {}
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchOffices(); }, [fetchOffices]);

  const handleCreate = async () => {
    if (!form.name || !form.admin_email || !form.admin_password) {
      toast.error('Nom du bureau, email et mot de passe requis'); return;
    }
    try {
      const res = await axios.post(`${API}/offices`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Bureau "${form.name}" créé`);
      setShowCreate(false);
      setForm({ name: '', admin_email: '', admin_password: '', admin_first_name: '', admin_last_name: '', can_manage_placements: false, can_manage_news: false });
      fetchOffices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (office) => {
    if (!window.confirm(`Supprimer le bureau "${office.name}" ? L'admin du bureau sera aussi supprimé.`)) return;
    try {
      await axios.delete(`${API}/offices/${office.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Bureau supprimé');
      fetchOffices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdateOffice = async () => {
    if (!editOffice) return;
    try {
      const payload = { name: editOffice.name };
      if (editOffice.admin_email) payload.admin_email = editOffice.admin_email;
      if (editOffice.admin_first_name) payload.admin_first_name = editOffice.admin_first_name;
      if (editOffice.admin_last_name) payload.admin_last_name = editOffice.admin_last_name;
      if (editOffice.new_password) payload.admin_password = editOffice.new_password;
      payload.can_manage_placements = editOffice.can_manage_placements || false;
      payload.can_manage_news = editOffice.can_manage_news || false;
      await axios.put(`${API}/offices/${editOffice.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Bureau mis à jour');
      setEditOffice(null);
      fetchOffices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="offices-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Bureaux</h1>
            <p className="text-sm text-muted-foreground">{offices.length} bureau{offices.length > 1 ? 'x' : ''}</p>
          </div>
          {!user?.office_id && (
            <Button onClick={() => setShowCreate(true)} style={{ backgroundColor: primaryColor }} data-testid="create-office-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau bureau
            </Button>
          )}
        </div>

        {offices.length === 0 && !loading ? (
          <Card className="bg-card/80"><CardContent className="p-12 text-center text-muted-foreground">
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Aucun bureau créé</p>
            <p className="text-xs mt-1">Créez un bureau pour organiser vos équipes</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {offices.map(office => (
              <Card key={office.id} className="bg-card/80 border-border/50 hover:border-primary/30 transition-all" data-testid={`office-${office.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Building2 className="w-4 h-4" style={{ color: primaryColor }} />
                      {office.name}
                    </CardTitle>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={async () => {
                        // Load admin info
                        let adminInfo = {};
                        if (office.admin_id) {
                          try {
                            const res = await axios.get(`${API}/users/${office.admin_id}`, { headers: { Authorization: `Bearer ${token}` } });
                            adminInfo = { admin_email: res.data.email, admin_first_name: res.data.first_name, admin_last_name: res.data.last_name };
                          } catch {}
                        }
                        setEditOffice({ ...office, ...adminInfo, new_password: '',
                          can_manage_placements: (office.permissions || {}).can_manage_placements || false,
                          can_manage_news: (office.permissions || {}).can_manage_news || false,
                        });
                      }}>
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(office)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="w-3 h-3" />
                      {office.clients_count || 0} clients
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <UserCog className="w-3 h-3" />
                      {office.analysts_count || 0} analystes
                    </div>
                  </div>
                  {office.admin && (
                    <div className="p-2 rounded bg-muted/30 text-xs">
                      <p className="font-medium">{office.admin.first_name} {office.admin.last_name}</p>
                      <p className="text-muted-foreground">{office.admin.email}</p>
                      <Badge variant={office.admin.status === 'active' ? 'default' : 'destructive'} className={`mt-1 text-[10px] ${office.admin.status === 'active' ? 'bg-green-500' : ''}`}>
                        {office.admin.status === 'active' ? 'Actif' : 'Désactivé'}
                      </Badge>
                    </div>
                  )}
                  {/* Permissions */}
                  <div className="flex gap-2 mt-2">
                    {(office.permissions?.can_manage_placements) && (
                      <Badge className="text-[10px] bg-blue-500/20 text-blue-400 border-blue-500/30">Placements</Badge>
                    )}
                    {(office.permissions?.can_manage_news) && (
                      <Badge className="text-[10px] bg-purple-500/20 text-purple-400 border-purple-500/30">Actualités</Badge>
                    )}
                    {(!office.permissions?.can_manage_placements && !office.permissions?.can_manage_news) && (
                      <span className="text-[10px] text-muted-foreground">Lecture seule</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="sm:max-w-md" data-testid="create-office-dialog">
            <DialogHeader>
              <DialogTitle>Nouveau bureau</DialogTitle>
              <DialogDescription>Un administrateur sera automatiquement créé pour ce bureau.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Nom du bureau</Label>
                <Input value={form.name} onChange={(e) => setForm(p => ({...p, name: e.target.value}))} placeholder="Bureau Paris, Régie Nord..." data-testid="office-name-input" />
              </div>
              <div className="border-t pt-3">
                <p className="text-xs text-muted-foreground mb-2">Identifiants de connexion du bureau</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Prénom" value={form.admin_first_name} onChange={(e) => setForm(p => ({...p, admin_first_name: e.target.value}))} />
                  <Input placeholder="Nom" value={form.admin_last_name} onChange={(e) => setForm(p => ({...p, admin_last_name: e.target.value}))} />
                </div>
                <Input className="mt-2" type="email" placeholder="Email du bureau" value={form.admin_email} onChange={(e) => setForm(p => ({...p, admin_email: e.target.value}))} data-testid="office-admin-email" />
                <Input className="mt-2 font-mono" type="text" placeholder="Mot de passe" value={form.admin_password} onChange={(e) => setForm(p => ({...p, admin_password: e.target.value}))} data-testid="office-admin-password" />
              </div>
              <div className="border-t pt-3 space-y-3">
                <p className="text-sm font-semibold">Autorisations du bureau</p>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Gérer les placements</Label>
                  <Switch checked={form.can_manage_placements} onCheckedChange={(v) => setForm(p => ({...p, can_manage_placements: v}))} data-testid="office-perm-placements" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Gérer les actualités</Label>
                  <Switch checked={form.can_manage_news} onCheckedChange={(v) => setForm(p => ({...p, can_manage_news: v}))} data-testid="office-perm-news" />
                </div>
              </div>
              <Button className="w-full" onClick={handleCreate} style={{ backgroundColor: primaryColor }} data-testid="submit-office-btn">
                Créer le bureau
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Office Dialog */}
        <Dialog open={!!editOffice} onOpenChange={() => setEditOffice(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Modifier le bureau</DialogTitle></DialogHeader>
            {editOffice && (
              <div className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label>Nom du bureau</Label>
                  <Input value={editOffice.name} onChange={(e) => setEditOffice(p => ({...p, name: e.target.value}))} data-testid="edit-office-name" />
                </div>
                <div className="border-t pt-3">
                  <p className="text-sm font-semibold mb-3">Admin du bureau</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Prénom</Label>
                      <Input value={editOffice.admin_first_name || ''} onChange={(e) => setEditOffice(p => ({...p, admin_first_name: e.target.value}))} placeholder="Prénom" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Nom</Label>
                      <Input value={editOffice.admin_last_name || ''} onChange={(e) => setEditOffice(p => ({...p, admin_last_name: e.target.value}))} placeholder="Nom" />
                    </div>
                  </div>
                  <div className="space-y-1 mt-2">
                    <Label className="text-xs">Email</Label>
                    <Input type="email" value={editOffice.admin_email || ''} onChange={(e) => setEditOffice(p => ({...p, admin_email: e.target.value}))} placeholder="email@exemple.com" />
                  </div>
                  <div className="space-y-1 mt-2">
                    <Label className="text-xs">Nouveau mot de passe (laisser vide pour ne pas changer)</Label>
                    <Input type="password" value={editOffice.new_password || ''} onChange={(e) => setEditOffice(p => ({...p, new_password: e.target.value}))} placeholder="Nouveau mot de passe" />
                  </div>
                </div>
                <div className="border-t pt-3 space-y-3">
                  <p className="text-sm font-semibold">Autorisations du bureau</p>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Gérer les placements</Label>
                    <Switch checked={editOffice.can_manage_placements || false} onCheckedChange={(v) => setEditOffice(p => ({...p, can_manage_placements: v}))} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Gérer les actualités</Label>
                    <Switch checked={editOffice.can_manage_news || false} onCheckedChange={(v) => setEditOffice(p => ({...p, can_manage_news: v}))} />
                  </div>
                </div>
                <Button className="w-full" onClick={handleUpdateOffice} data-testid="save-edit-office">Enregistrer</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
